import React, { useEffect } from 'react';

import LinearProgress from '@mui/material/LinearProgress';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Button,
    Card,
    Grid,
    ListItem,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';

import StyledH2 from 'common/components/StyledH2';

import * as actions from '../actions';
import * as selectors from '../selectors';

const CompanyUsers = ({
    companyUsers = [],
    fetchCompanyUsers,
    match,
    updateInstallerUsers,
    installerId,
    isFetchingCompanyUsers,
}) => {
    const { t } = useTranslation();
    const {
        params: { id },
    } = match;

    const handleClickUpdateInstallerUsers = () =>
        updateInstallerUsers(installerId, () => fetchCompanyUsers(id));

    const renderContent = ({ companyUsers, isFetchingCompanyUsers }) => {
        if (isFetchingCompanyUsers) return <LinearProgress />;
        if (isEmpty(companyUsers))
            return <ListItem>{t('No users found for this company.')}</ListItem>;
        if (companyUsers.length === 0)
            return <ListItem>{t('There are no items to display')}</ListItem>;
        return (
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                <DataGrid
                    columns={[
                        {
                            field: 'first_name',
                            headerName: t('Name'),
                            flex: 1,
                            renderCell: (params) => (
                                <Typography fontWeight="bold" variant="body2">
                                    {params.row.first_name}{' '}
                                    {params.row.last_name}
                                </Typography>
                            ),
                        },
                        {
                            field: 'company_group',
                            headerName: t('Role'),
                            flex: 1,
                            renderCell: (params) =>
                                params.row.company_group?.name || '',
                        },
                        { field: 'phone', headerName: t('Phone'), flex: 1 },
                        { field: 'email', headerName: t('Email'), flex: 1 },
                    ]}
                    rows={companyUsers}
                />
            </Box>
        );
    };

    useEffect(() => {
        fetchCompanyUsers(id);
    }, [id]);

    return (
        <Card>
            <Card.Header>
                <Grid container alignItems="center">
                    <Grid size="grow">
                        <StyledH2>{t('Users')}</StyledH2>
                    </Grid>

                    <Grid>
                        <Button
                            color="secondary"
                            onClick={handleClickUpdateInstallerUsers}
                            variant="text"
                        >
                            {t('Update')}
                        </Button>
                    </Grid>
                </Grid>
            </Card.Header>

            {renderContent({ companyUsers, isFetchingCompanyUsers })}
        </Card>
    );
};

const mapStateToProps = createStructuredSelector({
    companyUsers: selectors.getCompanyUsersData,
    isFetchingCompanyUsers: selectors.getCompanyUsersIsFetching,
});

const mapDispatchToProps = (dispatch) => ({
    fetchCompanyUsers: (id) => dispatch(actions.fetchCompanyUsers(id)),
    updateInstallerUsers: (id, callback) =>
        dispatch(actions.updateInstallerUsers(id, callback)),
});

CompanyUsers.propTypes = {
    companyUsers: PropTypes.array,
    fetchCompanyUsers: PropTypes.func,
    installerId: PropTypes.string,
    isFetchingCompanyUsers: PropTypes.bool,
    match: PropTypes.object,
    updateInstallerUsers: PropTypes.func,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(CompanyUsers));
